/**
 * Molar solubility from Ksp, with and without a common ion, and the ion-product test.
 *
 * For a salt AₘBₙ (m cations, n anions) in water, Ksp = [A]ᵐ[B]ⁿ with [A] = m·s and
 * [B] = n·s, so Ksp = mᵐ·nⁿ·s^(m+n) and s = (Ksp / (mᵐ·nⁿ))^(1/(m+n)). With ions already
 * present at concentrations c_A and c_B, the same equation reads
 * (m·s + c_A)ᵐ · (n·s + c_B)ⁿ = Ksp, which has exactly one positive root because the left
 * side rises with s; it is found by bisection, with no small-x approximation.
 */

import { decompose, type Salt } from "@/lib/tool-kit/chem-reference/ions";
import {
  KSP_TABLE,
  type KspEntry,
} from "@/lib/tool-kit/chem-reference/ksp-data";
import {
  formulaToUnicode,
  molarMassOf,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";

const WHITESPACE = /\s+/g;

export function pureWaterSolubility(ksp: number, m: number, n: number): number {
  return (ksp / (m ** m * n ** n)) ** (1 / (m + n));
}

/** The ion product Q = [A]ᵐ[B]ⁿ for concentrations in mol/L. */
export function ionProduct(
  cationConc: number,
  anionConc: number,
  m: number,
  n: number
): number {
  return cationConc ** m * anionConc ** n;
}

export type Saturation = "under" | "saturated" | "over";

const SATURATION_TOLERANCE = 1e-9;

/** Compare Q with Ksp: below → more can dissolve; above → a precipitate forms. */
export function compareQ(q: number, ksp: number): Saturation {
  const ratio = q / ksp;
  if (Math.abs(ratio - 1) <= SATURATION_TOLERANCE) {
    return "saturated";
  }
  return q > ksp ? "over" : "under";
}

export interface Solubility {
  /** mol of salt dissolving per litre. */
  molar: number;
  /** Equilibrium concentrations, mol/L. */
  cation: number;
  anion: number;
  /** True when the solution already held Q ≥ Ksp, so nothing more dissolves. */
  saturatedAlready: boolean;
}

const ITERATIONS = 200;

/**
 * Molar solubility with initial ion concentrations `cationInit` and `anionInit` (0 for
 * pure water).
 */
export function solubility(
  ksp: number,
  m: number,
  n: number,
  cationInit = 0,
  anionInit = 0
): Solubility {
  const initialQ = ionProduct(cationInit, anionInit, m, n);
  if (initialQ >= ksp) {
    return {
      molar: 0,
      cation: cationInit,
      anion: anionInit,
      saturatedAlready: true,
    };
  }
  const product = (s: number) =>
    ionProduct(m * s + cationInit, n * s + anionInit, m, n);
  let low = 0;
  // The pure-water value is an upper bound: extra ions only lower the solubility.
  let high = pureWaterSolubility(ksp, m, n);
  for (let i = 0; i < ITERATIONS; i += 1) {
    const mid = (low + high) / 2;
    if (product(mid) < ksp) {
      low = mid;
    } else {
      high = mid;
    }
  }
  const s = (low + high) / 2;
  return {
    molar: s,
    cation: m * s + cationInit,
    anion: n * s + anionInit,
    saturatedAlready: false,
  };
}

/* -------------------------------------------------------------------- table */

export interface TabulatedSalt extends KspEntry {
  salt: Salt;
  /** The formula with real subscripts: CaSO₄·2H₂O. */
  text: string;
}

function unicodeFormula(formula: string): string {
  const parsed = parseFormula(formula);
  return parsed.ok ? formulaToUnicode(parsed.formula) : formula;
}

export const TABULATED: readonly TabulatedSalt[] = KSP_TABLE.map((entry) => {
  const salt = decompose(entry.formula);
  if (!salt) {
    throw new Error(`Ksp entry ${entry.formula} does not decompose`);
  }
  return { ...entry, salt, text: unicodeFormula(entry.formula) };
});

/** The tabulated entry for a typed formula; CaSO4 finds CaSO4·2H2O. */
export function findTabulated(formula: string): TabulatedSalt | undefined {
  const typed = formula.replace(WHITESPACE, "");
  const exact = TABULATED.find((entry) => entry.formula === typed);
  if (exact) {
    return exact;
  }
  const salt = decompose(formula);
  return salt ? findBySalt(salt) : undefined;
}

/** The tabulated entry whose ions are this cation and anion. */
export function findBySalt(salt: Salt): TabulatedSalt | undefined {
  return TABULATED.find(
    (entry) =>
      entry.salt.cation.id === salt.cation.id &&
      entry.salt.anion.id === salt.anion.id
  );
}

/** Molar mass in g/mol of a formula as typed (hydrates included), or null. */
export function formulaMass(formula: string): number | null {
  const parsed = parseFormula(formula);
  return parsed.ok ? molarMassOf(parsed.formula.counts).total : null;
}
